// The draft's formatting on the clipboard: as HTML, which mail, documents, and chat paste
// formatted, and plain text for everywhere else; and from the HTML other apps copy, such as Docs,
// Word, or a web page, back to Markdown.
import {
  HIGHLIGHTS,
  TEXT_COLORS,
  pastedHighlight,
  pastedTextColor,
  type Highlight,
  type TextColor,
} from './colors';
import { hasFormatting, parseMarkdown, type Line, type MarkKind, type Span } from './markdown';
import { writeLines, type FormattedLine, type FormattedTable, type Marks } from './markdown-edit';

const ENTITIES: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
};
const escapeHtml = (text: string) => text.replace(/[&<>"]/gu, (char) => ENTITIES[char] ?? char);

/** A link that's safe to follow, to the web, an email address, or a phone, or null. */
export function safeHref(href: string | undefined): string | null {
  const value = href?.trim() ?? '';
  return /^(?:https?:|mailto:|tel:)/iu.test(value) ? value : null;
}

const LIST: ReadonlySet<Line['kind']> = new Set(['bullet', 'ordered', 'task']);
// What each kind of formatting is in HTML.
const HTML_TAGS: Readonly<Record<Exclude<MarkKind, 'link' | 'color' | 'highlight'>, string>> = {
  strong: 'strong',
  em: 'em',
  underline: 'u',
  strike: 's',
  sub: 'sub',
  sup: 'sup',
  code: 'code',
};

/** The outermost spans between `from` and `to`, in order, of those given. */
function outermost(spans: readonly Span[], from: number, to: number): Span[] {
  const inside = spans.filter((span) => span.start >= from && span.end <= to);
  return inside.filter(
    (span) =>
      !inside.some((other) => other !== span && other.start <= span.start && span.end <= other.end),
  );
}

/** A span's formatting as the HTML around its words. */
function around(span: Span, inner: string): string {
  switch (span.kind) {
    case 'link': {
      const href = safeHref(span.href);
      return href ? `<a href="${escapeHtml(href)}">${inner}</a>` : inner;
    }
    // Colors as styles, which mail and documents keep, as the colors they are on a white page.
    case 'color':
      return `<span style="color: ${TEXT_COLORS[(span.color as TextColor | undefined) ?? 'gray']}">${inner}</span>`;
    case 'highlight':
      return `<span style="background-color: ${HIGHLIGHTS[(span.color as Highlight | undefined) ?? 'yellow']}">${inner}</span>`;
    default: {
      const tag = HTML_TAGS[span.kind];
      return `<${tag}>${inner}</${tag}>`;
    }
  }
}

/** A line's words and their formatting as HTML, or a table cell's, between `from` and `to`. */
function inlineHtml(text: string, line: Line, from = line.from, to = line.end): string {
  const escapes = new Set(line.escapes);
  const plain = (start: number, end: number) => {
    let out = '';
    for (let at = start; at < end; at += 1) if (!escapes.has(at)) out += text[at] ?? '';
    return escapeHtml(out);
  };
  const write = (start: number, end: number): string => {
    let out = '';
    let at = start;
    for (const span of outermost(line.spans, start, end)) {
      out += plain(at, span.start);
      const inner = span.kind === 'code' ? plain(span.from, span.to) : write(span.from, span.to);
      out += around(span, inner);
      at = span.end;
    }
    return out + plain(at, end);
  };
  return write(from, to);
}

// A table's borders and room, written on it, as mail keeps only what's on its elements.
const CELL_STYLE = 'border: 1px solid #d0d7de; padding: 4px 8px; text-align: left;';

/**
 * The draft as HTML: lines in a row as one paragraph, a blank line between paragraphs, and
 * lists, headings, quotes, dividers, and code as what they are.
 */
export function markdownToHtml(text: string): string {
  const out: string[] = [];
  let paragraph: string[] = [];
  let quote: string[] = [];
  let code: string[] | null = null;
  let table: string[] | null = null;
  const lists: string[] = [];
  const endParagraph = () => {
    if (paragraph.length > 0) out.push(`<p>${paragraph.join('<br>')}</p>`);
    paragraph = [];
  };
  const endQuote = () => {
    if (quote.length > 0) out.push(`<blockquote><p>${quote.join('<br>')}</p></blockquote>`);
    quote = [];
  };
  const closeList = () => {
    out.push(`</li></${lists.pop() ?? 'ul'}>`);
  };
  const endTable = () => {
    if (table)
      out.push(`<table style="border-collapse: collapse;">${table.join('')}</tbody></table>`);
    table = null;
  };
  for (const line of parseMarkdown(text)) {
    if (code) {
      if (line.kind === 'fence') {
        out.push(`<pre><code>${code.map(escapeHtml).join('\n')}</code></pre>`);
        code = null;
      } else code.push(text.slice(line.start, line.end));
      continue;
    }
    const words = text.slice(line.from, line.end).trim();
    if (line.kind !== 'row' && line.kind !== 'delimiter') endTable();
    if (!LIST.has(line.kind)) while (lists.length > 0) closeList();
    if (line.kind !== 'paragraph' || !words) endParagraph();
    if (line.kind !== 'quote') endQuote();
    switch (line.kind) {
      case 'fence':
        code = [];
        break;
      case 'rule':
        out.push('<hr>');
        break;
      case 'delimiter':
        break;
      case 'row': {
        if (line.header) endTable();
        const tag = line.header ? 'th' : 'td';
        const cells = line.cells
          .map(
            (cell) =>
              `<${tag} style="${CELL_STYLE}">${inlineHtml(text, line, cell.from, cell.to)}</${tag}>`,
          )
          .join('');
        table ??= [];
        table.push(line.header ? `<thead><tr>${cells}</tr></thead><tbody>` : `<tr>${cells}</tr>`);
        break;
      }
      case 'heading': {
        const level = String(Math.min(line.level, 6));
        out.push(`<h${level}>${inlineHtml(text, line)}</h${level}>`);
        break;
      }
      case 'quote':
        quote.push(inlineHtml(text, line));
        break;
      case 'paragraph':
      case 'code':
        if (words) paragraph.push(inlineHtml(text, line));
        break;
      default: {
        const tag = line.kind === 'ordered' ? 'ol' : 'ul';
        while (lists.length > line.depth + 1) closeList();
        if (lists.length === line.depth + 1) {
          if (lists.at(-1) === tag) out.push('</li>');
          else closeList();
        }
        while (lists.length < line.depth + 1) {
          const start = Number.parseInt(line.number, 10);
          out.push(tag === 'ol' && start > 1 ? `<ol start="${String(start)}">` : `<${tag}>`);
          lists.push(tag);
        }
        const box = line.kind === 'task' ? (line.checked ? '☑ ' : '☐ ') : '';
        out.push(`<li>${box}${inlineHtml(text, line)}`);
      }
    }
  }
  if (code) out.push(`<pre><code>${code.map(escapeHtml).join('\n')}</code></pre>`);
  endTable();
  endParagraph();
  endQuote();
  while (lists.length > 0) closeList();
  return out.join('');
}

/** A link's address as people say it: without https:// or mailto:. */
const addressWords = (href: string) =>
  href.replace(/^(?:https?:\/\/(?:www\.)?|mailto:|tel:)/iu, '').replace(/\/$/u, '');

/**
 * The draft as plain text, for apps that don't take formatting: lists keep their bullets and
 * numbers, quotes their >, dividers their dashes, and links their address after their words; what
 * made words bold or a line a heading goes.
 */
export function plainCopy(text: string): string {
  if (!hasFormatting(text)) return text;
  const kept: string[] = [];
  for (const line of parseMarkdown(text)) {
    if (line.kind === 'fence') continue;
    if (line.kind === 'code' || line.kind === 'rule' || line.kind === 'delimiter') {
      kept.push(text.slice(line.start, line.end));
      continue;
    }
    const marker =
      LIST.has(line.kind) || line.kind === 'quote' ? text.slice(line.start, line.from) : '';
    const escapes = new Set(line.escapes);
    let words = '';
    const write = (from: number, to: number) => {
      let at = from;
      for (const span of outermost(line.spans, from, to)) {
        for (; at < span.start; at += 1) if (!escapes.has(at)) words += text[at] ?? '';
        if (span.kind === 'code') {
          for (let index = span.from; index < span.to; index += 1)
            if (!escapes.has(index)) words += text[index] ?? '';
        } else write(span.from, span.to);
        const said = text.slice(span.from, span.to);
        if (span.kind === 'link' && span.href && addressWords(span.href) !== addressWords(said))
          words += ` (${span.href.replace(/^mailto:/iu, '')})`;
        at = span.end;
      }
      for (; at < to; at += 1) if (!escapes.has(at)) words += text[at] ?? '';
    };
    // A table keeps its pipes, as Markdown's tables are read as text.
    if (line.kind === 'row') {
      const cells = line.cells.map((cell) => {
        words = '';
        write(cell.from, cell.to);
        return words.trimEnd().replace(/\|/gu, '\\|');
      });
      kept.push(`| ${cells.join(' | ')} |`);
      continue;
    }
    write(line.from, line.end);
    kept.push(marker + words);
  }
  return kept.join('\n');
}

// What isn't writing at all.
const SKIPPED = new Set([
  'SCRIPT',
  'STYLE',
  'HEAD',
  'TITLE',
  'META',
  'LINK',
  'TEMPLATE',
  'NOSCRIPT',
  'IFRAME',
  'OBJECT',
  'SVG',
  'CANVAS',
  'SELECT',
  'BUTTON',
  'IMG',
]);
const BLOCKS = new Set([
  'P',
  'DIV',
  'SECTION',
  'ARTICLE',
  'HEADER',
  'FOOTER',
  'MAIN',
  'ASIDE',
  'NAV',
  'FIGURE',
  'FIGCAPTION',
  'ADDRESS',
  'DL',
  'DT',
  'DD',
  'CENTER',
  'TABLE',
  'TBODY',
  'THEAD',
  'TFOOT',
]);

interface Context {
  readonly marks: Marks;
  /** The lists the writing is in, innermost last: what each item's marker starts with. */
  readonly lists: readonly { readonly ordered: boolean; number: number; readonly indent: string }[];
  readonly quote: boolean;
}

/**
 * Formatted writing pasted from another app, as Markdown: bold, italics, underline,
 * strikethrough, subscript, superscript, colors, highlights, code, links, headings, lists,
 * checklists, quotes, dividers, tables, and code blocks. Null when it has no formatting,
 * so the plain text that came with it is pasted instead, as it's written; but for a `file`,
 * which has no plain text with it, its writing, formatted or not.
 */
export function markdownFromHtml(html: string, file = false): string | null {
  if (!html || html.length > 2_000_000 || typeof DOMParser === 'undefined') return null;
  const document = new DOMParser().parseFromString(html, 'text/html');
  let lines: (Written | FormattedTable)[] = [];
  // The line being written, and whether a space is due before the next character in it.
  const at: { line: Written | null; space: boolean } = { line: null, space: false };

  const start = (marker: string, context: Context): Written => {
    const line: Written = { marker: context.quote && !marker ? '> ' : marker, chars: [] };
    lines.push(line);
    at.line = line;
    at.space = false;
    return line;
  };
  const end = () => {
    at.line = null;
    at.space = false;
  };
  const blankLine = () => {
    const last = lines.at(-1);
    if (last && !isBlank(last)) lines.push({ marker: '', chars: [] });
    end();
  };
  /** A table cell's writing, as one line, whatever lines and paragraphs it had. */
  const cellOf = (cell: Element, context: Context) => {
    const outside = { lines, line: at.line, space: at.space };
    lines = [];
    end();
    for (const child of cell.childNodes) walk(child, { ...context, lists: [], quote: false });
    const chars: Written['chars'] = [];
    for (const line of lines) {
      if (isTable(line) || line.chars.length === 0) continue;
      if (chars.length > 0) chars.push({ char: ' ', marks: {} });
      chars.push(...line.chars);
    }
    while (chars.at(-1)?.char === ' ') chars.pop();
    lines = outside.lines;
    at.line = outside.line;
    at.space = outside.space;
    return chars;
  };
  const write = (text: string, context: Context) => {
    for (const char of text) {
      if (/\s/u.test(char)) {
        at.space = true;
        continue;
      }
      const line = at.line ?? start('', context);
      if (at.space && line.chars.length > 0)
        line.chars.push({
          char: ' ',
          marks: shared(line.chars.at(-1)?.marks ?? {}, context.marks),
        });
      at.space = false;
      line.chars.push({ char, marks: context.marks });
    }
  };

  const walk = (node: Node, context: Context) => {
    if (node.nodeType === Node.TEXT_NODE) {
      write(node.textContent ?? '', context);
      return;
    }
    if (!(node instanceof Element) || SKIPPED.has(node.tagName.toUpperCase())) return;
    const tag = node.tagName.toUpperCase();
    const style = node.getAttribute('style') ?? '';
    const css = (node as HTMLElement).style as CSSStyleDeclaration | undefined;
    const children = (next: Context) => {
      for (const child of node.childNodes) walk(child, next);
    };

    // Word and Outlook write list items as paragraphs, their bullet in a span of its own.
    if (/mso-list:\s*Ignore/iu.test(style)) return;
    const msoList = /mso-list:\s*l\d+\s+level(\d)/iu.exec(style);
    if (msoList) {
      const level = Number(msoList[1] ?? 1) - 1;
      const bullet = node.querySelector('[style*="mso-list"]')?.textContent.trim() ?? '';
      const marker = /^\d+[.)]/u.test(bullet) ? `${bullet.replace(/[.)]$/u, '')}. ` : '- ';
      start(`${'  '.repeat(level)}${marker}`, context);
      children(context);
      end();
      return;
    }
    if (tag === 'BR') {
      end();
      start('', context);
      return;
    }
    if (tag === 'HR') {
      end();
      lines.push({ marker: '---', chars: [] });
      end();
      return;
    }
    const heading = /^H([1-6])$/u.exec(tag);
    if (heading) {
      start(`${'#'.repeat(Number(heading[1]))} `, context);
      children(context);
      end();
      return;
    }
    if (tag === 'PRE') {
      lines.push({ marker: '```', chars: [] });
      for (const text of node.textContent.replace(/\n$/u, '').split('\n'))
        lines.push({
          marker: '',
          chars: Array.from(text, (char) => ({ char, marks: {} })),
          raw: true,
        });
      lines.push({ marker: '```', chars: [] });
      end();
      return;
    }
    if (tag === 'BLOCKQUOTE') {
      end();
      children({ ...context, quote: true });
      end();
      return;
    }
    if (tag === 'UL' || tag === 'OL') {
      const parent = context.lists.at(-1);
      const indent = parent ? `${parent.indent}${' '.repeat(parent.ordered ? 3 : 2)}` : '';
      const first = Number.parseInt(node.getAttribute('start') ?? '1', 10);
      const list = { ordered: tag === 'OL', number: Number.isNaN(first) ? 1 : first, indent };
      end();
      children({ ...context, lists: [...context.lists, list] });
      end();
      return;
    }
    if (tag === 'LI') {
      const list = context.lists.at(-1) ?? { ordered: false, number: 1, indent: '' };
      const box = node.querySelector<HTMLInputElement>(
        ':scope > input[type="checkbox"], :scope > p > input[type="checkbox"]',
      );
      const words = node.textContent.trimStart();
      const task = box !== null || /^[☐☑]/u.test(words);
      const ticked = box ? box.checked || box.hasAttribute('checked') : words.startsWith('☑');
      const marker = task
        ? `${list.indent}- [${ticked ? 'x' : ' '}] `
        : list.ordered
          ? `${list.indent}${String(list.number)}. `
          : `${list.indent}- `;
      if (list.ordered) list.number += 1;
      const line = start(marker, context);
      children(context);
      // The box it was ticked with is in its marker now.
      if (task && /^[☐☑]$/u.test(line.chars[0]?.char ?? ''))
        line.chars.splice(0, line.chars[1]?.char === ' ' ? 2 : 1);
      end();
      return;
    }
    if (tag === 'INPUT') return;
    // A table of two columns or more, as Docs, Sheets, and Word copy one, stays a table. One laid
    // out with tables, as mail often is, or one with tables in it, is read as its writing.
    if (node instanceof HTMLTableElement) {
      const rows = [...node.rows];
      const layout =
        node.getAttribute('role') === 'presentation' ||
        node.querySelector('table') !== null ||
        Math.max(0, ...rows.map((row) => row.cells.length)) < 2;
      if (!layout) {
        end();
        lines.push(rows.map((row) => [...row.cells].map((cell) => cellOf(cell, context))));
        end();
        return;
      }
    }
    if (tag === 'TR') {
      end();
      [...node.children].forEach((cell, index) => {
        if (index > 0) {
          (at.line ?? start('', context)).chars.push({ char: '\t', marks: {} });
          at.space = false;
        }
        walk(cell, context);
      });
      end();
      return;
    }
    if (tag === 'P' || BLOCKS.has(tag)) {
      // Paragraphs with room between them, as on most pages, keep a blank line between them.
      const spaced =
        tag === 'P' && context.lists.length === 0 && !/margin(?:-bottom|-top)?:\s*0/iu.test(style);
      if (spaced) blankLine();
      // A block in a list item, or at the start of one, is the item's own line.
      if (at.line && at.line.chars.length > 0) end();
      children(context);
      if (spaced) blankLine();
      else if (!(context.lists.length > 0 && at.line?.chars.length === 0)) end();
      return;
    }

    const marks: { -readonly [K in keyof Marks]: Marks[K] } = { ...context.marks };
    const weight = css?.fontWeight ?? '';
    const light =
      weight === 'normal' ||
      weight === 'lighter' ||
      (/^\d+$/u.test(weight) && Number(weight) < 600);
    // Docs wraps what it copies in <b style="font-weight:normal">.
    if ((tag === 'B' || tag === 'STRONG') && !light) marks.strong = true;
    else if (light) marks.strong = false;
    if (/^(?:bold|bolder|[6-9]00)$/u.test(weight)) marks.strong = true;
    if (['I', 'EM', 'CITE', 'DFN', 'VAR'].includes(tag) || css?.fontStyle === 'italic')
      marks.em = true;
    if (
      ['S', 'STRIKE', 'DEL'].includes(tag) ||
      (css?.textDecoration ?? '').includes('line-through') ||
      style.includes('line-through')
    )
      marks.strike = true;
    if (['CODE', 'KBD', 'SAMP', 'TT'].includes(tag)) marks.code = true;
    if (tag === 'A') {
      const href = safeHref(node.getAttribute('href') ?? undefined);
      if (href) marks.href = href;
    }
    // A link's own underline, as Docs writes it, isn't the writer's.
    if (
      (tag === 'U' || tag === 'INS' || /text-decoration[^;]*underline/iu.test(style)) &&
      marks.href === undefined
    )
      marks.underline = true;
    const raise = css?.verticalAlign ?? '';
    if (tag === 'SUB' || raise === 'sub') {
      marks.sub = true;
      marks.sup = false;
    } else if (tag === 'SUP' || raise === 'super') {
      marks.sup = true;
      marks.sub = false;
    }
    // A color on the words' own element; one that's how a whole page reads, such as black, takes
    // a color away. A link keeps its own.
    const declared = css?.color ?? '';
    const color = declared ? declared : tag === 'FONT' ? (node.getAttribute('color') ?? '') : '';
    if (color && marks.href === undefined) marks.color = pastedTextColor(color) ?? undefined;
    // A highlight: <mark>, or a background behind the words, as Docs and Word write one.
    const written =
      /(?:background(?:-color)?|mso-highlight)\s*:\s*([^;]+)/iu.exec(style)?.[1]?.trim() ?? '';
    const background = [css?.backgroundColor, written].find(Boolean) ?? '';
    if (tag === 'MARK' && !background) marks.highlight = 'yellow';
    else if (background && !/^(?:transparent|initial|inherit|none|auto)$/iu.test(background))
      marks.highlight = pastedHighlight(background) ?? undefined;
    children({ ...context, marks });
  };

  walk(document.body, { marks: {}, lists: [], quote: false });

  // No more than one blank line in a row, none at either end, and no spaces at the ends of lines.
  const kept: (Written | FormattedTable)[] = [];
  for (const line of lines) {
    if (!isTable(line)) while (line.chars.at(-1)?.char === ' ') line.chars.pop();
    const previous = kept.at(-1);
    if (isBlank(line) && (!previous || isBlank(previous))) continue;
    kept.push(line);
  }
  for (let last = kept.at(-1); last && isBlank(last); last = kept.at(-1)) kept.pop();
  if (kept.length === 0) return null;
  const markdown = writeLines(kept);
  return file || hasFormatting(markdown) ? markdown : null;
}

type Written = { -readonly [K in keyof FormattedLine]: FormattedLine[K] } & {
  chars: { char: string; marks: Marks }[];
};
// ReadonlyArray isn't narrowed by Array.isArray.
const isTable = (line: Written | FormattedTable): line is FormattedTable => Array.isArray(line);
const isBlank = (line: Written | FormattedTable) =>
  !isTable(line) && line.chars.length === 0 && !line.marker;

/** The formatting a space between two characters shares with both, so it doesn't break it. */
function shared(a: Marks, b: Marks): Marks {
  return {
    ...(a.strong && b.strong && { strong: true }),
    ...(a.em && b.em && { em: true }),
    ...(a.underline && b.underline && { underline: true }),
    ...(a.strike && b.strike && { strike: true }),
    ...(a.sub && b.sub && { sub: true }),
    ...(a.sup && b.sup && { sup: true }),
    ...(a.code && b.code && { code: true }),
    ...(a.href !== undefined && a.href === b.href && { href: a.href }),
    ...(a.color !== undefined && a.color === b.color && { color: a.color }),
    ...(a.highlight !== undefined && a.highlight === b.highlight && { highlight: a.highlight }),
  };
}

/**
 * Copies writing in Markdown formatted, for the apps that paste it so, such as mail, Docs, or
 * Jira, and as plain text for the rest. Plain writing is copied as it is.
 */
export async function copyFormatted(text: string): Promise<void> {
  if (!hasFormatting(text)) {
    await navigator.clipboard.writeText(text);
    return;
  }
  if (typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard.write === 'function') {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([plainCopy(text)], { type: 'text/plain' }),
          'text/html': new Blob([markdownToHtml(text)], { type: 'text/html' }),
        }),
      ]);
      return;
    } catch {
      // Some browsers only copy text from a page.
    }
  }
  await navigator.clipboard.writeText(plainCopy(text));
}
